import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare,
  CheckCircle,
  XCircle,
  Send,
  TrendingUp,
  Building2,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Eye,
  RotateCcw,
  LayoutGrid,
  List,
  MoreHorizontal,
  Calendar,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { adminApi, getErrorMessage } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort, formatDateTime } from "@/utils/dateFormat";

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    delivered: {
      color:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      label: "Delivered",
    },
    sent: {
      color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      label: "Sent",
    },
    queued: {
      color:
        "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      label: "Queued",
    },
    pending: {
      color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
      label: "Pending",
    },
    failed: {
      color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
      label: "Failed",
    },
    undelivered: {
      color:
        "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
      label: "Undelivered",
    },
  };

  const { color, label } = config[status] || {
    color: "bg-gray-100 text-gray-700",
    label: status,
  };

  return <Badge className={color}>{label}</Badge>;
};

// Message type badge
const TypeBadge = ({ type }) => {
  const config = {
    verification: {
      color: "bg-purple-100 text-purple-700",
      label: "Verification",
    },
    reminder: { color: "bg-blue-100 text-blue-700", label: "Reminder" },
    completion: { color: "bg-green-100 text-green-700", label: "Completion" },
    expiration: { color: "bg-orange-100 text-orange-700", label: "Expiration" },
    signing_link: {
      color: "bg-primary/10 text-primary",
      label: "Signing Link",
    },
    in_person: { color: "bg-cyan-100 text-cyan-700", label: "In-Person" },
    custom: { color: "bg-gray-100 text-gray-700", label: "Custom" },
  };

  const { color, label } = config[type] || {
    color: "bg-gray-100 text-gray-700",
    label: type,
  };

  return (
    <Badge variant="outline" className={color}>
      {label}
    </Badge>
  );
};

// Stat card component
const StatCard = ({
  title,
  value,
  description,
  icon: Icon,
  trend,
  color = "primary",
}) => {
  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    green:
      "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    red: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
    yellow:
      "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400",
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {trend && (
          <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
            <TrendingUp className="h-3 w-3" />
            {trend}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// SMS message card for grid view
const SMSCard = ({ message, onView, onResend, actionLoading, user }) => (
  <Card className="group hover:shadow-md transition-shadow">
    {/* Header */}
    <div className="h-16 bg-primary/10 flex items-center justify-center relative">
      <MessageSquare className="h-6 w-6 text-primary" />

      {/* Actions */}
      <div className="absolute top-2 right-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(message)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {(message.status === "failed" ||
              message.status === "undelivered") && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onResend(message.id)}
                  disabled={actionLoading}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Resend
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Status badge */}
      <div className="absolute bottom-2 left-2">
        <StatusBadge status={message.status} />
      </div>
    </div>

    {/* Content */}
    <CardContent className="p-4">
      <div className="mb-2">
        <h3 className="font-medium truncate">
          {message.recipient_name || "Unknown"}
        </h3>
        <p className="text-sm text-muted-foreground">
          {message.recipient_phone_formatted}
        </p>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <TypeBadge type={message.message_type} />
      </div>

      {message.organisation_name && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
          <Building2 className="h-3 w-3" />
          <span className="truncate">{message.organisation_name}</span>
        </div>
      )}

      <div className="pt-3 border-t text-xs text-muted-foreground flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        {formatDateShort(message.created_at, user)}
      </div>
    </CardContent>
  </Card>
);

export const AdminSMS = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  // UI State
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [filters, setFilters] = useState({
    status: "",
    messageType: "",
    search: "",
  });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");

  // Memoized query params for messages
  const messagesQueryParams = useMemo(
    () => ({
      ...filters,
      page,
      limit: 20,
    }),
    [page, filters],
  );

  // Query: Fetch dashboard data
  const {
    data: dashboard,
    isLoading: dashboardLoading,
    isFetching: dashboardFetching,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ["admin", "sms", "dashboard"],
    queryFn: async () => {
      const response = await adminApi.getSMSDashboard();
      return response.data.data;
    },
  });

  // Query: Fetch provider status
  const {
    data: providerStatus,
    isLoading: providerLoading,
    refetch: refetchProviderStatus,
  } = useQuery({
    queryKey: ["admin", "sms", "provider-status"],
    queryFn: async () => {
      const response = await adminApi.getSMSProviderStatus();
      return response.data.data;
    },
  });

  // Query: Fetch messages with pagination and filters
  const { data: messagesData, isFetching: messagesFetching } = useQuery({
    queryKey: ["admin", "sms", "messages", messagesQueryParams],
    queryFn: async () => {
      const response = await adminApi.getSMSMessages(messagesQueryParams);
      return response.data.data;
    },
    placeholderData: (previousData) => previousData,
  });

  // Extract data from queries
  const messages = messagesData?.messages || [];
  const pagination = {
    page,
    limit: 20,
    total: messagesData?.total || 0,
    pages: messagesData?.pages || 0,
  };
  const loading = dashboardLoading || providerLoading;
  const isFetching = dashboardFetching;

  // Mutation: Resend SMS
  const resendMutation = useMutation({
    mutationFn: async (messageId) => {
      const response = await adminApi.resendSMS(messageId);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.sms.resent"));
      queryClient.invalidateQueries({ queryKey: ["admin", "sms", "messages"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "sms", "dashboard"],
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t("admin.sms.resendFailed")));
    },
  });

  // Mutation: Send test SMS
  const sendTestMutation = useMutation({
    mutationFn: async ({ phone, message }) => {
      const response = await adminApi.sendTestSMS({ phone, message });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.data.success) {
        toast.success(t("admin.sms.testSms.success"));
        setShowTestDialog(false);
        setTestPhone("");
        setTestMessage("");
      } else {
        toast.error(data.data.error || t("admin.sms.testSms.failed"));
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t("admin.sms.testSms.failed")));
    },
  });

  // Combined action loading state
  const actionLoading = resendMutation.isPending || sendTestMutation.isPending;

  // Handler: Refresh all data
  const handleRefresh = () => {
    refetchDashboard();
    refetchProviderStatus();
  };

  // Handler: View message details
  const handleViewMessage = async (message) => {
    try {
      const response = await adminApi.getSMSMessage(message.id);
      setSelectedMessage(response.data.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error(getErrorMessage(error, t("admin.sms.loadDetailsFailed")));
    }
  };

  // Handler: Resend message
  const handleResendMessage = (messageId) => {
    if (!confirm(t("admin.sms.resendConfirm"))) return;
    resendMutation.mutate(messageId);
  };

  // Handler: Send test SMS
  const handleSendTest = () => {
    if (!testPhone) {
      toast.error(t("admin.sms.testSms.phoneRequired"));
      return;
    }
    sendTestMutation.mutate({ phone: testPhone, message: testMessage });
  };

  const renderMessagesContent = () => {
    if (messages.length === 0) {
      return (
        <Card>
          <CardContent className="text-center py-8 text-muted-foreground">
            {t("sms.history.noMessages")}
          </CardContent>
        </Card>
      );
    }
    return viewMode === "grid" ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {messages.map((message) => (
          <SMSCard
            key={message.id}
            message={message}
            user={user}
            onView={handleViewMessage}
            onResend={handleResendMessage}
            actionLoading={actionLoading}
          />
        ))}
      </div>
    ) : (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("sms.history.recipient")}</TableHead>
                <TableHead>{t("sms.history.type")}</TableHead>
                <TableHead>{t("sms.history.status")}</TableHead>
                <TableHead>{t("team.organisationName")}</TableHead>
                <TableHead>{t("sms.history.date")}</TableHead>
                <TableHead className="w-25">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {messages.map((message) => (
                <TableRow key={message.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {message.recipient_name || "Unknown"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {message.recipient_phone_formatted}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <TypeBadge type={message.message_type} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={message.status} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {message.organisation_name || "-"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateShort(message.created_at, user)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewMessage(message)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {(message.status === "failed" ||
                        message.status === "undelivered") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleResendMessage(message.id)}
                          disabled={actionLoading}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-9" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const { overview, topOrganizations, recentFailures } = dashboard || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("admin.sms.title")}
          </h1>
          <p className="text-muted-foreground">{t("admin.sms.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={providerStatus?.configured ? "default" : "destructive"}
            className="gap-1"
          >
            <div
              className={`h-2 w-2 rounded-full ${
                providerStatus?.configured ? "bg-green-500" : "bg-red-500"
              }`}
            />
            Twilio{" "}
            {providerStatus?.configured
              ? t("sms.provider.connected")
              : t("sms.provider.notConfigured")}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTestDialog(true)}
          >
            <Send className="h-4 w-4 mr-2" />
            {t("admin.sms.testSms.send")}
          </Button>
          <Button variant="outline" size="icon" onClick={handleRefresh}>
            <RefreshCw
              className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </div>

      {/* Provider Warning */}
      {!providerStatus?.configured && (
        <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <div className="flex gap-3">
            <div className="shrink-0">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/10">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t("sms.notConfigured.adminTitle")}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("sms.notConfigured.adminDescription")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t("sms.stats.totalMessages")}
          value={overview?.totalMessages?.toLocaleString() || 0}
          description={t("sms.stats.deliveryRate", {
            rate: overview?.deliveryRate || 0,
          })}
          icon={MessageSquare}
          color="primary"
        />
        <StatCard
          title={t("sms.stats.delivered")}
          value={overview?.delivered?.toLocaleString() || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title={t("sms.stats.failed")}
          value={overview?.failed?.toLocaleString() || 0}
          icon={XCircle}
          color="red"
        />
        <StatCard
          title={t("sms.stats.creditsUsed")}
          value={overview?.totalCreditsUsed?.toLocaleString() || 0}
          description={`${t("sms.stats.uniqueRecipients")}: ${
            overview?.uniqueRecipients || 0
          }`}
          icon={TrendingUp}
          color="blue"
        />
      </div>

      <Tabs defaultValue="messages" className="space-y-4">
        <TabsList>
          <TabsTrigger value="messages">
            {t("admin.sms.tabs.messages")}
          </TabsTrigger>
          <TabsTrigger value="failures">
            {t("admin.sms.tabs.failures")}
          </TabsTrigger>
          <TabsTrigger value="organisations">
            {t("admin.sms.tabs.organisations")}
          </TabsTrigger>
        </TabsList>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-50">
                  <Input
                    placeholder={t("placeholders.searchByPhoneNameMessage")}
                    value={filters.search}
                    onChange={(e) => {
                      setFilters({ ...filters, search: e.target.value });
                      setPage(1);
                    }}
                    className="max-w-sm"
                  />
                </div>
                <Select
                  value={filters.status || "all"}
                  onValueChange={(value) => {
                    setFilters({
                      ...filters,
                      status: value === "all" ? "" : value,
                    });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-37.5">
                    <SelectValue placeholder={t("sms.history.filterStatus")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("sms.history.filterStatus")}
                    </SelectItem>
                    <SelectItem value="delivered">
                      {t("sms.status.delivered")}
                    </SelectItem>
                    <SelectItem value="sent">{t("sms.status.sent")}</SelectItem>
                    <SelectItem value="failed">
                      {t("sms.status.failed")}
                    </SelectItem>
                    <SelectItem value="pending">
                      {t("sms.status.pending")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.messageType || "all"}
                  onValueChange={(value) => {
                    setFilters({
                      ...filters,
                      messageType: value === "all" ? "" : value,
                    });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-37.5">
                    <SelectValue placeholder={t("sms.history.filterType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("sms.history.filterType")}
                    </SelectItem>
                    <SelectItem value="verification">
                      {t("sms.templates.types.verification")}
                    </SelectItem>
                    <SelectItem value="reminder">
                      {t("sms.templates.types.reminder")}
                    </SelectItem>
                    <SelectItem value="completion">
                      {t("sms.templates.types.completion")}
                    </SelectItem>
                    <SelectItem value="expiration">
                      {t("sms.templates.types.expiration")}
                    </SelectItem>
                    <SelectItem value="signing_link">
                      {t("sms.templates.types.signingLink")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilters({ status: "", messageType: "", search: "" });
                    setPage(1);
                  }}
                >
                  {t("common.clear")}
                </Button>

                {/* View toggle */}
                <div className="flex items-center border rounded-lg p-1">
                  <Button
                    variant={viewMode === "grid" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setViewMode("grid")}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setViewMode("list")}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Messages Grid/Table */}
          {renderMessagesContent()}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * pagination.limit + 1} to{" "}
                {Math.min(page * pagination.limit, pagination.total)} of{" "}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1 || messagesFetching}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === pagination.pages || messagesFetching}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Failures Tab */}
        <TabsContent value="failures">
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.sms.recentFailures")}</CardTitle>
              <CardDescription>
                {t("admin.sms.recentFailuresDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("sms.history.recipient")}</TableHead>
                    <TableHead>{t("sms.history.type")}</TableHead>
                    <TableHead>{t("common.error")}</TableHead>
                    <TableHead>{t("sms.history.date")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentFailures?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center py-8 text-muted-foreground"
                      >
                        {t("admin.sms.noRecentFailures")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentFailures?.map((failure) => (
                      <TableRow key={failure.id}>
                        <TableCell>
                          {failure.recipient_phone_formatted}
                        </TableCell>
                        <TableCell>
                          <TypeBadge type={failure.message_type} />
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="text-red-600">
                              {failure.provider_error_code ||
                                t("common.unknown")}
                            </span>
                            {failure.provider_error_message && (
                              <p className="text-muted-foreground truncate max-w-50">
                                {failure.provider_error_message}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateShort(failure.created_at, user)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organisations Tab */}
        <TabsContent value="organisations">
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.topOrgsBySms")}</CardTitle>
              <CardDescription>
                {t("admin.orgsHighestSmsUsage")}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.organisation")}</TableHead>
                    <TableHead className="text-right">
                      {t("sms.messagesSent")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("billing.creditsUsed")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topOrganizations?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    topOrganizations?.map((org, index) => (
                      <TableRow key={org.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium">
                              {index + 1}
                            </div>
                            <span className="font-medium">{org.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {org.messageCount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {org.creditsUsed.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Message Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("common.messageDetails")}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">
                    {t("common.recipient")}
                  </Label>
                  <p className="font-medium">
                    {selectedMessage.recipient_name || "Unknown"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedMessage.recipient_phone_formatted}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("common.status")}
                  </Label>
                  <div className="mt-1">
                    <StatusBadge status={selectedMessage.status} />
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("common.type")}
                  </Label>
                  <div className="mt-1">
                    <TypeBadge type={selectedMessage.message_type} />
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("clauses.segments")}
                  </Label>
                  <p className="font-medium">
                    {selectedMessage.message_segments}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("common.created")}
                  </Label>
                  <p className="text-sm">
                    {formatDateTime(selectedMessage.created_at)}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("common.delivered")}
                  </Label>
                  <p className="text-sm">
                    {selectedMessage.delivered_at
                      ? formatDateTime(selectedMessage.delivered_at)
                      : "-"}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">
                  {t("common.messageContent")}
                </Label>
                <div className="mt-1 p-3 bg-muted rounded-lg text-sm">
                  {selectedMessage.message_content}
                </div>
              </div>

              {selectedMessage.provider_error_message && (
                <div>
                  <Label className="text-red-600">{t("common.error")}</Label>
                  <div className="mt-1 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300">
                    <strong>{selectedMessage.provider_error_code}:</strong>{" "}
                    {selectedMessage.provider_error_message}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">
                    {t("admin.providerMessageId")}
                  </Label>
                  <p className="font-mono text-xs">
                    {selectedMessage.provider_message_id || "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("admin.requestId")}
                  </Label>
                  <p className="font-mono text-xs">
                    {selectedMessage.request_id || "-"}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetailDialog(false)}
            >
              Close
            </Button>
            {selectedMessage &&
              (selectedMessage.status === "failed" ||
                selectedMessage.status === "undelivered") && (
                <Button
                  onClick={() => handleResendMessage(selectedMessage.id)}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-2" />
                  )}
                  Resend
                </Button>
              )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test SMS Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.sendTestSms")}</DialogTitle>
            <DialogDescription>
              Send a test message to verify your Twilio configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("common.phoneNumber")}</Label>
              <Input
                placeholder="+61412345678"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t("common.enterPhoneE164")}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t("common.messageOptional")}</Label>
              <Textarea
                placeholder={t("placeholders.testMessage")}
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={actionLoading || !testPhone}
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSMS;
